import React, { useCallback, useRef, useState, type ReactNode } from 'react';

import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ThirdPartyPromptSuggestionsController } from './ThirdPartyPromptSuggestionsController';
import type { UsePromptSuggestionsReturn } from './types';
import { usePromptSuggestionsWithDecision } from './usePromptSuggestionsWithDecision';
import type { ThirdPartySuggestedPromptTile } from './useThirdPartySuggestedPromptTile';

export type PromptSuggestionsProps = AssistanceServiceInputParam & {
	includeThirdParty: boolean;
	children: (suggestions: UsePromptSuggestionsReturn) => ReactNode;
};

const PENDING_DECISION: ThirdPartySuggestedPromptTile = { enable3pPrompt: false, ready: false };
const ORDINARY_DECISION: ThirdPartySuggestedPromptTile = { enable3pPrompt: false, ready: true };

/** Only the optional controller changes identity; the consumer UI stays mounted. */
export const PromptSuggestions = ({
	includeThirdParty,
	children,
	assistanceServiceParams,
}: PromptSuggestionsProps): React.JSX.Element => {
	const hasReportedExposureRef = useRef(false);
	const [controllerState, setControllerState] = useState({
		includeThirdParty,
		decision: PENDING_DECISION,
	});
	// Do not reuse a previous controller's ready flag when it is mounted again.
	if (controllerState.includeThirdParty !== includeThirdParty) {
		setControllerState({ includeThirdParty, decision: PENDING_DECISION });
	}
	const onDecisionChange = useCallback(
		(decision: ThirdPartySuggestedPromptTile) => {
			setControllerState((current) =>
				current.includeThirdParty === includeThirdParty &&
				current.decision.enable3pPrompt === decision.enable3pPrompt &&
				current.decision.ready === decision.ready
					? current
					: { includeThirdParty, decision },
			);
		},
		[includeThirdParty],
	);
	const suggestions = usePromptSuggestionsWithDecision({
		assistanceServiceParams,
		includeThirdParty,
		thirdPartyPrompt: includeThirdParty ? controllerState.decision : ORDINARY_DECISION,
	});
	return (
		<>
			{includeThirdParty && (
				<ThirdPartyPromptSuggestionsController
					assistanceServiceParams={assistanceServiceParams}
					onDecisionChange={onDecisionChange}
					hasReportedExposureRef={hasReportedExposureRef}
				/>
			)}
			<React.Fragment key="consumer">{children(suggestions)}</React.Fragment>
		</>
	);
};
